/**
 * Screening module constants
 */

import type { MatchLevel, MatchColors } from './screening.types';

export const MATCH_COLORS: Record<MatchLevel, MatchColors> = {
  excellent: {
    bg: 'bg-gradient-to-r from-emerald-50 to-green-50',
    border: 'border-emerald-200',
    badge: 'text-emerald-600',
    progress: 'bg-gradient-to-r from-emerald-500 to-green-500',
    icon: 'text-emerald-700',
  },
  good: {
    bg: 'bg-gradient-to-r from-blue-50 to-cyan-50',
    border: 'border-blue-200',
    badge: 'text-blue-600',
    progress: 'bg-gradient-to-r from-blue-500 to-cyan-500',
    icon: 'text-blue-500',
  },
  fair: {
    bg: 'bg-gradient-to-r from-amber-50 to-yellow-50',
    border: 'border-amber-200',
    badge: 'text-amber-600',
    progress: 'bg-gradient-to-r from-amber-500 to-yellow-500',
    icon: 'text-amber-600',
  },
  poor: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    badge: 'text-slate-400',
    progress: 'bg-slate-400',
    icon: 'text-slate-500',
  },
};

export const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const ACCEPTED_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.csv', '.xls', '.xlsx'];

export const FILE_FORMAT_LABELS = ['PDF', 'DOCX', 'CSV', 'XLS'];

export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Get match level from score
 */
export const getMatchLevelFromScore = (score: number): MatchLevel => {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'poor';
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Generate unique file ID
 */
export const generateFileId = (): string => `file_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

/**
 * Filter files by allowed types and size
 */
export const filterAllowedFiles = (files: FileList): File[] => {
  const allowed: File[] = [];

  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;

    if (
      (ACCEPTED_FILE_TYPES.includes(file.type) || ACCEPTED_FILE_EXTENSIONS.includes(extension))
      && file.size <= MAX_FILE_SIZE_BYTES
    ) {
      allowed.push(file);
    }
  }

  return allowed;
};

/**
 * Convert file to base64
 */
export const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => {
    const result = reader.result as string;
    // Remove data URL prefix (e.g., "data:application/pdf;base64,")
    const base64 = result.split(',')[1];
    resolve(base64);
  };
  reader.onerror = (error) => reject(error);
});
